import { Big } from 'big.js';

type ToPercentNumberOptions = {
  /**
   * The value to return if the conversion fails.
   *
   * @defaultValue 0
   */
  fallback?: number;
  /**
   * The power of 10 by which to divide the input value.
   *
   * @defaultValue 2
   * @example
   * // 2 -> divides by 100 (10^2)
   * // 3 -> divides by 1000 (10^3)
   */
  scaleFactor?: number;
};

const DEFAULT_OPTIONS = {
  fallback: 0,
  scaleFactor: 2,
} satisfies ToPercentNumberOptions;

/**
 * Converts a user-friendly percentage value (ex.: `10` for 10%) into its mathematical decimal equivalent (ex.: `0.10`).
 * It uses the `big.js` library to ensure precision during division.
 *
 * @remarks
 * This utility ensures a finite number is always returned by handling common edge cases:
 * - Returns the `fallback` for `null`, `undefined`, or empty strings (`''`).
 * - Parses strings using {@link Number.parseFloat} and returns the `fallback` if the result is `NaN` or `Infinity`.
 *
 * @example
 * ```ts
 * toPercentNumber(10); // returns 0.1
 * toPercentNumber('10'); // returns 0.1
 * toPercentNumber(null); // returns 0
 * toNumber(null, { fallback: -1 }); // returns -1
 * toPercentNumber(10, { scaleFactor: 3 }); // returns 0.01 (10 / 1000)
 * ```
 *
 * @param value - The percentage value to convert.
 * @param options - Optional configuration object {@link ToPercentNumberOptions}.
 *
 * @returns The converted decimal value as a number, or the fallback value.
 */
export const toPercentNumber = (value: number | null | string | undefined, options?: ToPercentNumberOptions) => {
  if (value === null || value === undefined || value === '') {
    return options?.fallback ?? DEFAULT_OPTIONS.fallback;
  }

  const exponent = options?.scaleFactor ?? DEFAULT_OPTIONS.scaleFactor;
  const normalisedValue = typeof value === 'number' ? value : Number.parseFloat(value);

  if (!Number.isFinite(normalisedValue)) {
    return options?.fallback ?? DEFAULT_OPTIONS.fallback;
  }

  return new Big(normalisedValue).div(10 ** exponent).toNumber();
};
